import React = require('react');
import { Formik, FormikProps } from 'formik';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';

import { Messages } from '../../codegen/Localize';
import { UserSettings } from '../services/resources/UserSettings';
import { UserSettingsStyle } from '../styles/UserSettingsStyle';
import _ = require('underscore');

import { beaconComponentRegistryEntry, getUserAccountSettingsURL } from '~src/beacon/BeaconComponentRegistryFunctions';
import { DeepLink } from '~src/beacon/deepLink/DeepLink';
import { FormikIntegerStepper } from '~src/commonInfra/components/FormikField';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';

import { FormikCheckbox } from '../../commonInfra/components/FormikCheckbox';
import { AccessibilitySettingsProps } from './AccessibilitySettings';
import { InlineCancelAndSaveButtons } from './UserSettingEditButtons';

const DEEPLINK_ID = 'toast-auto-dismiss';
const AUTO_DISMISS_FORMIK_NAME = 'toastAutoDismiss';
const TIME_FORMIK_NAME = 'toastAutoDismissTimeout';
const MAX_INT = 2147483647;
const TIME_LABEL_ID = 'toast-auto-dismiss-time';
export const DEFAULT_AUTO_DISMISS_TIME = 5;
export const TOAST_AUTO_DISMISS_TEST_ID = 'toast-auto-dismiss';
export const TOAST_AUTO_DISMISS_TIME_TEST_ID = 'toast-auto-dismiss-time';

export interface ToastAutoDismissField {
    [AUTO_DISMISS_FORMIK_NAME]: boolean;
    [TIME_FORMIK_NAME]: number;
}

function showButtons(formik: FormikProps<ToastAutoDismissField>, autoDismiss: boolean, toastAutoDismissTimeout: number) {
    if (!formik.touched || !formik.isValid || formik.isSubmitting || (_.isEqual(formik.values[AUTO_DISMISS_FORMIK_NAME], autoDismiss) && _.isEqual(formik.values[TIME_FORMIK_NAME], toastAutoDismissTimeout))) {
        return false;
    }
    return true;
}

export function ToastAutoDismiss(props: AccessibilitySettingsProps): JSX.Element {
    const [currentAutoDismiss, setCurrentAutoDismiss] = React.useState(false);
    const [currentToastAutoDismissTimeout, setCurrentToastAutoDismissTimeout] = React.useState(5);

    async function handleSubmit(values: ToastAutoDismissField) {
        const newTimeout = values[AUTO_DISMISS_FORMIK_NAME] ? values[TIME_FORMIK_NAME] : 0;
        const newAutoDismiss = newTimeout !== 0;
        UserSettings.updateToastAutoDismissSetting(newTimeout).then(() => {
            setCurrentAutoDismiss(newAutoDismiss);
            setCurrentToastAutoDismissTimeout(newTimeout);
            getSessionManagementServiceInstance().refreshSessionInfo().finally(() => {
                ToasterService.instance.pop({ type: ToastType.Success, text: Messages.settingsUpdateSuccess(), testId: TOAST_AUTO_DISMISS_TEST_ID });
            });
        }).catch(() => {
            ToasterService.instance.pop({ type: ToastType.Error, text: Messages.settingsUpdateFailure() });
        });
    }

    React.useEffect(
        () => {
            UserSettings.getToastAutoDismissSetting()
                .then((result: number) => {
                    const autoDismiss = result !== 0;
                    setCurrentAutoDismiss(autoDismiss);
                    // when the time stepper starts disabled, start its value with the default time
                    setCurrentToastAutoDismissTimeout(autoDismiss ? result : DEFAULT_AUTO_DISMISS_TIME);
                }).catch(() => { });
        },
        []
    );

    return (
        <Formik
            initialValues={{ toastAutoDismiss: currentAutoDismiss, toastAutoDismissTimeout: currentToastAutoDismissTimeout }}
            onSubmit={(values: ToastAutoDismissField) => handleSubmit(values)}
            enableReinitialize={true}
        >
            {
                (formik: FormikProps<ToastAutoDismissField>) => (
                    <DeepLink deepLinkId={DEEPLINK_ID}>
                        <div style={{ display: 'flex' }}>
                                <div data-tb-test-id={`userSettings-${TOAST_AUTO_DISMISS_TEST_ID}`}>
                                    <div className={UserSettingsStyle.settingsLabelText}>
                                        {Messages.toastAutoDismissUserSettingAlerts()}
                                    </div>
                                </div>
                          <div className ={UserSettingsStyle.settingsLabelDescription}>
                              <div className={UserSettingsStyle.detailsValue}>
                                  {Messages.toastAutoDismissUserSettingDetail()}
                              </div>
                              <div className={UserSettingsStyle.detailsValue}>
                                  <FormikCheckbox
                                      containerStyle={{ marginLeft: 0 }}
                                      disabled={props.userHasNoSiteId}
                                      label={Messages.toastAutoDismissUserSettingEnable()}
                                      name={AUTO_DISMISS_FORMIK_NAME}
                                      testId={TOAST_AUTO_DISMISS_TEST_ID}
                                  />
                              </div>
                              <div className={UserSettingsStyle.detailsValue}>
                                  <label id={TIME_LABEL_ID}>{Messages.toastAutoDismissUserSettingTimeoutLabel()}</label>
                                  <FormikIntegerStepper
                                      aria-labelledby={TIME_LABEL_ID}
                                      containerStyle={{ marginLeft: 6 }}
                                      disabled={props.userHasNoSiteId || !formik.values[AUTO_DISMISS_FORMIK_NAME]}
                                      label={null}
                                      onValueUpdated={(value) => formik.setFieldValue(TIME_FORMIK_NAME, value)}
                                      max={MAX_INT}
                                      min={1}
                                      name={TIME_FORMIK_NAME}
                                      start={currentToastAutoDismissTimeout ? currentToastAutoDismissTimeout : DEFAULT_AUTO_DISMISS_TIME}
                                      testId={TOAST_AUTO_DISMISS_TIME_TEST_ID}
                                  />
                              </div>
                              {
                                  showButtons(formik, currentAutoDismiss, currentToastAutoDismissTimeout) &&
                                  <InlineCancelAndSaveButtons
                                      testId={TOAST_AUTO_DISMISS_TEST_ID}
                                      formik={formik}
                                  />
                              }
                          </div>
                        </div>
                    </DeepLink>
                )
            }
        </Formik>
    );
}

export const ToastAutoDismissSettingBeaconConfig = beaconComponentRegistryEntry({
  command: () =>  Messages.toastAutoDismissUserSettingAlerts(),
  deeplinkId: DEEPLINK_ID,
  url: (props) => getUserAccountSettingsURL(props.sessionInfo),
  tags:  () => { return [Messages.toastAutoDismissUserSettingDetail(), Messages.userSettings_Accessibility_Header()]; },
  isAdmin: false,
});
